import type { GenericAbortSignal } from 'axios'
import { API_MASTER_URL } from '../constants/base-urls'
import type {
  IMasterBank,
  IMasterCity,
  IMasterCountry,
  IMasterDistrict,
  IMasterEducation,
  IMasterEducationLevel,
  IMasterFileType,
  IMasterGender,
  IMasterIndustryType,
  IMasterJobFunction,
  IMasterMaritalStatus,
  IMasterProvince,
  IMasterReason,
  IMasterReligion,
  IMasterSubDistrict,
  IPaginationParam,
  IPaginationResponse,
  ITimezone,
} from '../types'
import { createAxiosInstance } from '../utils'

const axios = createAxiosInstance({
  baseURL: API_MASTER_URL,
  withAuth: true,
})

/**
 * Area
 *
 */
export const fetchCountries = (params: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IPaginationResponse<IMasterCountry> }>(`/area/country`, { params, signal }).then(({ data }) => data.data)
}

export const fetchProvinces = (params: { country: string } & IPaginationParam, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IPaginationResponse<IMasterProvince> }>(`/area/province`, { params, signal }).then(({ data }) => data.data)
}

export const fetchCities = (params: { province?: string; country?: string } & IPaginationParam, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IPaginationResponse<IMasterCity> }>(`/area/city`, { params, signal }).then(({ data }) => data.data)
}

export const fetchDistricts = (params: { city: string } & IPaginationParam, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IPaginationResponse<IMasterDistrict> }>(`/area/district`, { params, signal }).then(({ data }) => data.data)
}

export const fetchSubDistrict = (params: { district: string } & IPaginationParam, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IMasterSubDistrict> }>(`/area/sub-district`, { params, signal })
    .then(({ data }) => data.data)
}

/**
 * Education Level
 *
 */
export const fetchEducationLevel = (params?: IPaginationParam & { keyword: string }, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IMasterEducationLevel> }>(`/education-level`, { params, signal })
    .then((response) => response.data.data)
}

/**
 * Education
 *
 */
export const fetchEducations = (params: IPaginationParam & { type: 'INSTITUTION' | 'MAJOR'; q?: string; educationLevelId?: string }) => {
  return axios
    .get<{ data: IPaginationResponse<IMasterEducation> }>(`/education-suggestion`, { params })
    .then((response) => response.data.data)
}

/**
 * Genders
 *
 */
export const fetchGenders = () => {
  return axios.get<{ data: { content: Array<IMasterGender> } }>(`/gender`).then((response) => response.data.data)
}

/**
 * Religions
 *
 */
export const fetchReligions = () => {
  return axios.get<{ data: { content: Array<IMasterReligion> } }>(`/religion`).then((response) => response.data.data)
}

/**
 * Marital Status
 *
 */
export const fetchMaritalStatus = () => {
  return axios.get<{ data: { content: Array<IMasterMaritalStatus> } }>(`/marital-status`).then((response) => response.data.data)
}

/**
 * File Types
 *
 */
export const fetchFileTypes = () => {
  return axios.get<{ data: { content: Array<IMasterFileType> } }>(`/filetype`).then((response) => response.data.data)
}

/**
 * Reasons
 *
 */
export const fetchReasons = (params: { type: 'blacklist' | 'reject' | 'withdraw' } & IPaginationParam, signal?: GenericAbortSignal) => {
  return axios.get<{ data: { content: Array<IMasterReason> } }>(`/reason`, { params, signal }).then((response) => response.data.data)
}

/**
 * Category Job Functions
 *
 */
export const fetchCategoryJobFunctions = (params?: IPaginationParam) => {
  return axios
    .get<{ data: IPaginationResponse<IMasterJobFunction> }>(`/job-function/category`, { params })
    .then((response) => response.data.data)
}

/**
 * Job Functions
 *
 */
export const fetchJobFunctions = (params?: IPaginationParam) => {
  return axios.get<{ data: IPaginationResponse<IMasterJobFunction> }>(`/job-function`, { params }).then((response) => response.data.data)
}

/**
 * Industry type
 *
 */
export const fetchIndustryTypes = () => {
  return axios.get<{ data: { content: IMasterIndustryType[] } }>(`/industry-type`).then((response) => response.data.data)
}

/**
 * Nationality
 *
 */
export const fetchNationalities = () => {
  return axios.get<{ data: { content: IMasterIndustryType[] } }>(`/nationality`).then((response) => response.data.data)
}

/**
 * Bank
 *
 */
export const fetchBanks = (params?: IPaginationParam) => {
  return axios.get<{ data: IPaginationResponse<IMasterBank> }>(`/bank`, { params }).then((response) => response.data.data)
}

/**
 * Timezones
 *
 */
export const fetchTimezones = (params?: IPaginationParam) => {
  return axios.get<{ data: IPaginationResponse<ITimezone> }>(`/timezone`, { params }).then((response) => response.data.data)
}

export const downloadBanks = () => {
  return axios
    .get(`/bank/export`, {
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
      },
    })
    .then((response) => {
      return response
    })
}
